import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTerms } from '../../contexts/TermsContext';
import { useToast } from '../../contexts/ToastContext';
import { termsService } from '../../services/termsService';
import { ApiError } from '../../services/apiClient';
import { TermsModal } from '../common/TermsModal';
import type { TermsDocumentStatus } from '../../types/terms';

// SW-91: the digital partner onboarding consent gate. Rendered by
// ProtectedRoute over whatever page the user landed on — the portal shell
// mounts normally underneath, this modal blocks interaction with it until
// all required documents are accepted. Deliberately NOT dismissable (no
// close button, no backdrop-click-to-close, no Escape handler) since
// acceptance is mandatory, unlike every other modal in the portal.
export const TermsAcceptanceModal: React.FC = () => {
  const { t } = useTranslation();
  const { token, signOut } = useAuth();
  const { documents, refetch } = useTerms();
  const { showToast } = useToast();

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [openDocument, setOpenDocument] = useState<TermsDocumentStatus | null>(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allChecked = documents.length > 0 && documents.every((doc) => checked[doc.key]);

  const handleSubmit = async () => {
    if (!token || !allChecked) return;

    setError('');
    setIsSubmitting(true);

    try {
      await termsService.acceptTerms(token);
      await refetch();
      showToast('success', t('termsGate.acceptedSuccess', 'Warunki zostały zaakceptowane'));
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || t('termsGate.acceptError', 'Nie udało się zaakceptować warunków'));
      } else {
        setError(t('termsGate.acceptError', 'Nie udało się zaakceptować warunków'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (documents.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-brand-primary-deep rounded-xl w-full max-w-2xl shadow-2xl">
        {/* Header — no close button, this gate can't be dismissed */}
        <div className="px-6 py-4 border-b border-white/10">
          <h2 className="text-xl font-semibold text-white">
            {t('termsGate.title', 'Zaakceptuj warunki współpracy')}
          </h2>
          <p className="mt-1 text-sm text-white/60">
            {t(
              'termsGate.subtitle',
              'Zanim przejdziesz do portalu, potwierdź poniższe dokumenty. Zajmie to chwilę.'
            )}
          </p>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-brand-warm/20 border border-brand-warm/40 rounded-lg">
              <p className="text-sm text-brand-warm">{error}</p>
            </div>
          )}

          <div className="space-y-3">
            {documents.map((doc) => (
              <label
                key={doc.key}
                className="flex items-start gap-3 p-4 bg-surface-card rounded-lg cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={!!checked[doc.key]}
                  onChange={(e) =>
                    setChecked((prev) => ({ ...prev, [doc.key]: e.target.checked }))
                  }
                  className="mt-1 h-4 w-4 rounded border-border-strong text-brand-primary focus:ring-2 focus:ring-brand-primary"
                  disabled={isSubmitting}
                />
                <span className="text-sm text-ink-primary">
                  {t('termsGate.acceptDocument', 'Akceptuję')}{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenDocument(doc);
                    }}
                    className="text-brand-primary hover:text-brand-primary-hover underline"
                  >
                    {doc.title}
                  </button>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!allChecked || isSubmitting}
            className="w-full py-3 px-4 bg-brand-primary text-white font-semibold rounded-lg hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {isSubmitting && (
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            )}
            {isSubmitting
              ? t('termsGate.submitting', 'Zapisywanie...')
              : t('termsGate.submit', 'Akceptuję i kontynuuję')}
          </button>
          <button
            type="button"
            onClick={signOut}
            className="w-full mt-2 py-2 text-sm text-white/60 hover:text-white/80 transition-colors"
          >
            {t('common.logout', 'Wyloguj się')}
          </button>
        </div>
      </div>

      {openDocument && (
        <TermsModal
          isOpen={!!openDocument}
          onClose={() => setOpenDocument(null)}
          type="dataProcessing"
          titleOverride={openDocument.title}
          contentOverride={openDocument.content}
        />
      )}
    </div>
  );
};
